import React, { FC, useCallback, useState } from 'react';
import { CircularProgress, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { SupportedTerraSDKType } from '@demind-inc/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useAuthContext, useTerraWidget, useWearablesContext } from '../../../../../data-access';
import { useRemoveUserDevice } from '../../../../../data-access/mutations';
import { Path } from '../../../../../routing';
import { MobileSdkOnlyWearbales, WEARABLES } from '../../../../SideBar/constants';
import './WearablesSection.scss';

const WearablesSection: FC = () => {
  const { t } = useTranslation();
  const { terraWidget, isPending: isGeneratingWidget } = useTerraWidget();
  const [clickedId, setClickedId] = useState<string | null>(null);
  const [disconnectingDeviceId, setDisconnectingDeviceId] = useState<string | null>(null);

  const { user } = useAuthContext();
  const { invalidTerraUserIds, allDevices } = useWearablesContext();
  const { removeUserDevice } = useRemoveUserDevice();

  const handleConnect = useCallback(
    (id: string) => {
      setClickedId(id);
      terraWidget({ metricId: user.metricId, redirectTo: Path.DASHBOARD, sourceType: 'ALL' });
    },
    [terraWidget, user.metricId]
  );

  const getIsConnected = useCallback(
    (id: string) => allDevices?.some((device) => device.terraManufacturer === id),
    [allDevices]
  );

  const getHasError = useCallback(
    (id: string) => {
      const targetDevice = allDevices?.find((device) => device.terraManufacturer === id);

      if (!targetDevice) {
        return false;
      }

      return invalidTerraUserIds.includes(targetDevice.terraUserId);
    },
    [allDevices, invalidTerraUserIds]
  );

  const getDeviceForWearable = useCallback(
    (id: string) => allDevices?.find((device) => device.terraManufacturer === id),
    [allDevices]
  );

  const handleDisconnect = useCallback(
    async (deviceId: string) => {
      if (!user.deviceGroupId) {
        return;
      }

      setDisconnectingDeviceId(deviceId);

      try {
        await removeUserDevice({ deviceGroupId: user.deviceGroupId, deviceId });
      } finally {
        setDisconnectingDeviceId(null);
      }
    },
    [removeUserDevice, user.deviceGroupId]
  );

  return (
    <Stack gap={2} className="wearables-section">
      <Typography variant="h3" className="wearables-section__title">
        {t('settings.nav.healthData')}
      </Typography>

      <div className="wearables-section__block">
        <Stack className="wearables-section__list" gap={2}>
          {WEARABLES.map(({ id, name, Icon }, index) => {
            const connectedDevice = getDeviceForWearable(id);
            const deviceId = connectedDevice?.deviceId;
            const isConnected = getIsConnected(id);
            const hasError = getHasError(id);
            const isOnlyMobileSdkSupport = MobileSdkOnlyWearbales.includes(
              id as SupportedTerraSDKType
            );
            const isConnecting = isGeneratingWidget && clickedId === id;
            const isDisconnecting = disconnectingDeviceId === deviceId;

            const showSpinner = isConnecting || isDisconnecting;

            let actionLabel = t('settings.common.connect');
            let actionType: 'connect' | 'disconnect' | 'reconnect' = 'connect';
            let isActionDisabled = false;
            let actionHandler: (() => void) | undefined = () => handleConnect(id);

            if (hasError) {
              actionLabel = t('settings.common.reconnect');
              actionType = 'reconnect';
              actionHandler = () => handleConnect(id);
            } else if (isConnected) {
              actionLabel = t('settings.common.disconnect');
              actionType = 'disconnect';
              actionHandler = deviceId ? () => handleDisconnect(deviceId) : undefined;
              isActionDisabled = !deviceId;
            } else if (isOnlyMobileSdkSupport) {
              isActionDisabled = true;
            }

            return (
              <Stack key={id} className="wearables-section__list-item-wrapper" gap={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Icon className="wearables-section__list-item-icon" />
                    <Typography variant="body1" className="wearables-section__list-item-title">
                      {name}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" gap={1}>
                    {hasError && (
                      <Tooltip
                        title={t('settings.healthData.reconnectError', { name })}
                        arrow
                        placement="top"
                      >
                        <ErrorOutline
                          className="wearables-section__list-item-error-icon"
                          tabIndex={0}
                          aria-label={t('settings.healthData.reconnectError', { name })}
                        />
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        !isConnected && isOnlyMobileSdkSupport
                          ? t('settings.healthData.connectViaMobileOnly')
                          : ''
                      }
                    >
                      <span>
                        <button
                          type="button"
                          className={clsx(
                            'wearables-section__action-button',
                            `wearables-section__action-button--${actionType}`,
                            isActionDisabled && 'wearables-section__action-button--disabled'
                          )}
                          onClick={() => {
                            if (!isActionDisabled && actionHandler) {
                              actionHandler();
                            }
                          }}
                          disabled={isActionDisabled || showSpinner}
                        >
                          {showSpinner ? (
                            <CircularProgress size={14} />
                          ) : (
                            <Typography
                              variant="body2"
                              className="wearables-section__action-button-label"
                            >
                              {actionLabel}
                            </Typography>
                          )}
                        </button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>

                {index !== WEARABLES.length - 1 && (
                  <Divider className="wearables-section__list-item-divider" />
                )}
              </Stack>
            );
          })}
        </Stack>
      </div>
    </Stack>
  );
};

export default WearablesSection;
